/**
 * Apply-by-email **handoff** — the application leaves the SEEKER's own mailbox.
 *
 * We deliberately do not send application email from `lokerdollar.com`. Cold
 * application mail at volume degrades the From-domain reputation that magic-link
 * login depends on (both sign from the apex with DMARC `p=none`), and an employer
 * who replies to a relayed address is replying to us, not to the candidate.
 * Instead we build the complete draft — tailored cover letter, résumé, subject —
 * and hand it to whatever mail client the seeker already uses. They press send.
 *
 * Consequences, all of them good for the product:
 *  - zero apex-domain deliverability exposure (we never touch SMTP here);
 *  - the employer's Reply lands in the candidate's inbox by construction, so the
 *    reply relay (`apply+<trackerId>@apply.lokerdollar.com`, 0 replies ever) is
 *    unnecessary;
 *  - a human reads every address before it is used, so a DERIVED
 *    `careers@<domain>` is reviewable rather than blastable — which is what lifts
 *    the usable-inventory ceiling off the 243 extracted-address jobs.
 *
 * See docs/plans/email-apply-channel-2026-07-28.md (plan v2).
 */

import {
	buildGmailComposeUrl,
	type JobApplyEmailDraft,
} from "@/lib/apply/gmail";

/** The channels a draft can be opened in. `eml` is a download, not a URL. */
export type HandoffChannel = "gmail" | "outlook" | "mailto" | "eml" | "copy";

/**
 * Body cap for URL-scheme channels. `mailto:` is handed to the OS, and the
 * historical ceiling is the ~2,000-char IE/Windows shell limit; long bodies are
 * silently TRUNCATED by some clients rather than rejected, which would ship a
 * half-finished application. The `.eml` and clipboard channels carry the full
 * body, so nothing is lost — we only shorten what the URL must hold.
 */
export const MAILTO_BODY_MAX = 1500;

/**
 * Percent-encode one `mailto:` header value.
 *
 * NOT `URLSearchParams` — it encodes a space as `+`, which is correct for
 * `application/x-www-form-urlencoded` query strings but NOT for `mailto:`
 * (RFC 6068 §2), where mail clients render the `+` literally. Every space in a
 * cover letter would arrive as a plus sign.
 */
function encodeMailtoValue(value: string): string {
	return encodeURIComponent(value);
}

/** Truncate on a word boundary and mark the cut so the seeker knows to finish it. */
export function capBody(body: string, max: number = MAILTO_BODY_MAX): string {
	if (body.length <= max) return body;
	const cut = body.slice(0, max);
	const lastBreak = Math.max(cut.lastIndexOf("\n"), cut.lastIndexOf(" "));
	return `${cut.slice(0, lastBreak > max * 0.6 ? lastBreak : max).trimEnd()}\n…`;
}

/**
 * `mailto:` URL for the seeker's default mail app (Apple Mail, Outlook desktop,
 * Thunderbird, or the OS handler a browser maps to Gmail). Body is capped —
 * see {@link MAILTO_BODY_MAX}.
 */
export function buildMailtoUrl(draft: JobApplyEmailDraft): string {
	const subject = encodeMailtoValue(draft.subject);
	const body = encodeMailtoValue(capBody(draft.body));
	const cc = draft.cc ? `&cc=${encodeMailtoValue(draft.cc)}` : "";
	const bcc = draft.bcc ? `&bcc=${encodeMailtoValue(draft.bcc)}` : "";
	return `mailto:${encodeMailtoValue(draft.to)}?subject=${subject}${cc}${bcc}&body=${body}`;
}

/**
 * Outlook-on-the-web compose deeplink. Covers work/Microsoft 365 accounts, which
 * `mailto:` only reaches if the user registered Outlook as the OS mail handler.
 */
export function buildOutlookComposeUrl(draft: JobApplyEmailDraft): string {
	const params = new URLSearchParams({
		to: draft.to,
		subject: draft.subject,
		body: capBody(draft.body, 4000),
		...(draft.cc ? { cc: draft.cc } : {}),
		...(draft.bcc ? { bcc: draft.bcc } : {}),
	});
	return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
}

/** Every URL-openable channel for one draft. `.eml` is served by its own route. */
export function buildHandoffUrls(draft: JobApplyEmailDraft): {
	gmail: string;
	outlook: string;
	mailto: string;
} {
	return {
		gmail: buildGmailComposeUrl(draft),
		outlook: buildOutlookComposeUrl(draft),
		mailto: buildMailtoUrl(draft),
	};
}

export interface EmlAttachment {
	filename: string;
	/** MIME type, e.g. `application/pdf`. */
	type: string;
	content: ArrayBuffer;
}

/** Base64 in 76-column lines (RFC 2045 §6.8), chunked so a multi-MB résumé
 *  never blows the argument limit of `String.fromCharCode`. */
function base64Lines(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let binary = "";
	const CHUNK = 0x8000;
	for (let i = 0; i < bytes.length; i += CHUNK) {
		binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
	}
	const b64 = btoa(binary);
	const lines: string[] = [];
	for (let i = 0; i < b64.length; i += 76) lines.push(b64.slice(i, i + 76));
	return lines.join("\r\n");
}

/**
 * Encode a header value that may contain non-ASCII (an Indonesian company name,
 * an accented role title) as RFC 2047 encoded-words. Raw UTF-8 in a header is
 * illegal and renders as mojibake in most desktop clients.
 */
function encodeHeader(value: string): string {
	// Printable-ASCII only ⇒ legal as-is; anything else needs encoding.
	if (/^[\x20-\x7e]*$/.test(value)) return value;
	const utf8 = new TextEncoder().encode(value);
	let binary = "";
	for (const b of utf8) binary += String.fromCharCode(b);
	return `=?UTF-8?B?${btoa(binary)}?=`;
}

/**
 * Build a complete `.eml` (RFC 5322) message the browser downloads and the
 * seeker's desktop mail client opens as a ready-to-send draft.
 *
 * **This is the only handoff channel that can carry the résumé as a real
 * attachment** — no URL scheme (`mailto:`, Gmail compose, Outlook deeplink) can
 * attach a file, by design. The other channels compensate by downloading the CV
 * separately and inlining the résumé link/text in the body.
 *
 * No `Date` header: a draft the user has not sent yet should not claim a send
 * time, and clients stamp it on send anyway.
 */
export function buildEmlMessage(opts: {
	from: string;
	draft: JobApplyEmailDraft;
	attachment?: EmlAttachment | null;
}): string {
	const { from, draft, attachment } = opts;
	const headers = [
		`From: ${encodeHeader(from)}`,
		`To: ${encodeHeader(draft.to)}`,
		...(draft.cc ? [`Cc: ${encodeHeader(draft.cc)}`] : []),
		...(draft.bcc ? [`Bcc: ${encodeHeader(draft.bcc)}`] : []),
		`Subject: ${encodeHeader(draft.subject)}`,
		"MIME-Version: 1.0",
		"X-Unsent: 1", // Outlook: open as an editable draft, not a received message.
	];
	const body = draft.body.replace(/\r?\n/g, "\r\n");

	if (!attachment) {
		return [
			...headers,
			'Content-Type: text/plain; charset="utf-8"',
			"Content-Transfer-Encoding: 8bit",
			"",
			body,
			"",
		].join("\r\n");
	}

	const boundary = `----lokerdollar-${Math.random().toString(36).slice(2)}`;
	return [
		...headers,
		`Content-Type: multipart/mixed; boundary="${boundary}"`,
		"",
		`--${boundary}`,
		'Content-Type: text/plain; charset="utf-8"',
		"Content-Transfer-Encoding: 8bit",
		"",
		body,
		"",
		`--${boundary}`,
		`Content-Type: ${attachment.type}; name="${attachment.filename}"`,
		"Content-Transfer-Encoding: base64",
		`Content-Disposition: attachment; filename="${attachment.filename}"`,
		"",
		base64Lines(attachment.content),
		"",
		`--${boundary}--`,
		"",
	].join("\r\n");
}

/**
 * Alternate mailboxes to offer when the address was DERIVED from the company
 * domain rather than extracted from the posting. `careers@` is the global
 * convention and stays first; `hrd@` / `recruitment@` are the common Indonesian
 * variants. Shown as one-tap alternates — we never guess a single winner and we
 * never send to any of them ourselves.
 */
export function deriveAlternateApplyEmails(applyEmail: string): string[] {
	const at = applyEmail.lastIndexOf("@");
	if (at <= 0) return [];
	const local = applyEmail.slice(0, at).toLowerCase();
	const domain = applyEmail.slice(at + 1);
	return ["careers", "hrd", "recruitment", "jobs", "hr"]
		.filter((box) => box !== local)
		.map((box) => `${box}@${domain}`);
}
